/**
 * Detect ground vehicles by backend flag or callsign pattern.
 * Works across all views (live map, airport, history).
 *
 * Known patterns:
 * - Backend `ground_vehicle` (live feed) / `groundVehicle` (History rows) flag —
 *   persisted classification: DF=18/TC=2 ident, or a transmitter that only ever
 *   sent surface positions (never airborne) for days with no known identity
 * - DAA* (Dublin Airport Authority vehicles: DAAR13, DAAOPS1, DAAM81, etc.)
 * - TWR* (tower vehicles)
 * - Type designator TWR / GND — the Mictronics aircraft database (the backend's
 *   registration/type source since 2026-10) gives fixed tower transmitters and
 *   ground vehicles these pseudo-types, usually with no callsign at all
 *
 * - TXLU* (Dublin surface vehicles)
 *
 * TXLU was dropped from this list on 2026-09-17 because TXLU03 was seen at
 * 8,000 ft and read as a second emitter aboard Irish Air Corps AW139 276.
 * That was the wrong reading: a Dublin ground vehicle transmits the SAME ICAO
 * hex (4CA331) as the helicopter, so the altitude belonged to the helicopter
 * and the callsign to the vehicle. The callsigns are always distinct, so the
 * callsign is the reliable discriminator and TXLU is unconditional.
 */

// Callsign prefixes that are always ground vehicles
const GROUND_VEHICLE_PREFIXES = ['DAA', 'TWR', 'TXLU'];

// Pseudo type designators for non-aircraft transmitters (never real ICAO types)
const GROUND_VEHICLE_TYPES = new Set(['TWR', 'GND']);

/**
 * Check if an aircraft/entry is a ground vehicle.
 * @param {object} entry - Aircraft or arrival entry with callsign/flight/ground_vehicle fields
 * @returns {boolean} true if ground vehicle
 */
export function isGroundVehicle(entry) {
  if (!entry) return false;

  // Backend flag: live feed (snake_case) or History summary (camelCase)
  if (entry.ground_vehicle === true || entry.groundVehicle === true) return true;

  // Callsign-based detection (works for airport arrivals and history too)
  const callsign = entry.flight || entry.callsign || '';
  const trimmed = callsign.trim().toUpperCase();
  if (trimmed) {
    for (const prefix of GROUND_VEHICLE_PREFIXES) {
      if (trimmed.startsWith(prefix)) return true;
    }
  }

  // Type-based detection: live feed `t`, History/airport rows `aircraftType`
  const type = (entry.t || entry.aircraftType || '').trim().toUpperCase();
  if (GROUND_VEHICLE_TYPES.has(type)) return true;

  // Operator field check (for airport arrivals where operator = "DAA")
  const operator = entry.operator || '';
  if (operator.trim().toUpperCase() === 'DAA') return true;

  return false;
}
